/**
 * Area-mode global state.
 *
 * Area-mode is a *map state* — distinct from any tool. A polygon has been
 * drawn and the map is in a mode where the user can inspect sections, toggle
 * load cases, edit mastpoints via the Toolbar, etc. This module owns the
 * canonical state for that mode.
 *
 * Lives in a `.svelte.ts` module so we can use Svelte 5 runes (`$state`,
 * `$derived`) at module scope. Components import `areaModeState` and read
 * properties directly — Svelte's compiler wires up fine-grained reactivity
 * automatically:
 *
 *     import { areaModeState } from '…/area-mode-state.svelte';
 *     const isActive = $derived(areaModeState.active);
 *
 * Non-rune modules (plain `.ts`) can't read `$state` reactively. If a non-UI
 * module needs to react to state changes, convert it to `.svelte.ts` and use
 * `$effect.root(() => $effect(() => …))` for module-lifetime subscriptions.
 */

import geojsonArea from '@mapbox/geojson-area';
import { get as idbGet, set as idbSet } from 'idb-keyval';
import type { Entity } from 'cesium';

import type { ISectionInfo } from '../map-tools-store';
import type { IGeoJsonPolygonModel } from '$lib/models/IGeoJsonPolygonModel';
import { broadcastSectionCalculationStale } from '$lib/stores/polygon-section-stale-store';
import { POLYGON_SECTION_CALCULATION_IDB_KEY } from './areaToolUtils';

class AreaModeState {
	/** True once a polygon is drawn and area-mode is the active map state. */
	active = $state(false);

	/** GeoJSON polygon (closed ring). */
	polygon = $state<IGeoJsonPolygonModel['polygon'] | null>(null);

	/** The Cesium polygon entity that's drawn on the map. */
	polygonEntity = $state<Entity | null>(null);

	/** Polygon boundary ring as [lng, lat] pairs (not closed). */
	polygonBoundariesLngLat = $state<[number, number][]>([]);

	/** Polygon area in m² (computed from boundaries via `@mapbox/geojson-area`). */
	polygonAreaInSquareMeters = $state(0);

	/** Section info from the `/info` (and `/calculate`) endpoint. */
	sectionInfo = $state<ISectionInfo | null>(null);
	sectionInfoLoading = $state(false);
	sectionInfoError = $state<string | null>(null);

	/** Names of load cases currently visualised on the map. */
	activeLoadCases = $state<string[]>([]);

	/**
	 * Hover shows the worst stored load case per component - poles by largest calculated JBD,
	 * everything else by largest utilization - instead of only the active case. The map geometry
	 * is still driven by `activeLoadCases`; this is a value choice for the tooltip, not new
	 * geometry. Switched off automatically when the user ticks a single case.
	 */
	worstCaseMode = $state(true);

	/**
	 * Which backend endpoint last populated `sectionInfo.loadCases`:
	 *   - `'info'` — stiff structural calculation (`/PolygonSection/info`).
	 *   - `'calculate'` — full DSS run (`/PolygonSection/calculate`).
	 *   - `null` — no section info yet, or it has been cleared.
	 *
	 * Drives the small label above the "Lasttilfeller" pill so users know
	 * which calculation produced the values they're toggling between.
	 */
	loadCaseSource = $state<'info' | 'calculate' | null>(null);

	/** Toggles visibility of `LineTerrain` measurement entities. */
	showDistances = $state(false);

	/**
	 * Line colouring mode for the section panel:
	 *   - `'section'` — every line painted with its section-circuit colour.
	 *   - `'loadcase'` — load-case duplicates painted with the load case's
	 *     own `rgb`; plan/live originals (visible only when no active load
	 *     case covers them) keep the section-circuit colour.
	 */
	colorMode = $state<'section' | 'loadcase'>('section');

	/**
	 * When `true`, the right-side panel is collapsed to a small floating chip.
	 * When `false`, the full `PolygonSelectionDetails` panel is shown.
	 */
	panelMinimized = $state(false);

	// ── Derived ────────────────────────────────────────────────────────────

	readonly loadCases = $derived(this.sectionInfo?.loadCases ?? []);

	// ── Mutators ───────────────────────────────────────────────────────────

	/**
	 * Records the polygon boundary ring AND the resulting area in one call.
	 * Mirrors the old `mapToolsStore.setPolygonAreaInSquareMetersBasedOnPolygonBoundaries`
	 * + `setPolygonBoundariesLngLat` pair.
	 */
	setBoundariesAndArea(polygonBoundariesLngLat: [number, number][]): void {
		this.polygonBoundariesLngLat = polygonBoundariesLngLat;
		const area = geojsonArea.geometry({
			type: 'Polygon',
			coordinates: [polygonBoundariesLngLat]
		});
		// Two decimal places (the `* 100 / 100` is a no-op carried from the
		// pre-migration helper — left in for parity).
		this.polygonAreaInSquareMeters = Math.ceil((area * 100) / 100);
	}

	/** Minimises the right-side panel to the chip. */
	minimize(): void {
		this.panelMinimized = true;
	}

	/** Restores the right-side panel from chip. */
	maximize(): void {
		this.panelMinimized = false;
	}

	/** Resets every field to its default — called by `deactivateAreaMode`. */
	reset(): void {
		this.active = false;
		this.polygon = null;
		this.polygonEntity = null;
		this.polygonBoundariesLngLat = [];
		this.polygonAreaInSquareMeters = 0;
		this.sectionInfo = null;
		this.sectionInfoLoading = false;
		this.sectionInfoError = null;
		this.activeLoadCases = [];
		this.worstCaseMode = true;
		this.loadCaseSource = null;
		this.showDistances = false;
		this.colorMode = 'section';
		this.panelMinimized = false;
	}
}

export const areaModeState = new AreaModeState();

/**
 * Mark the IDB-cached section-calculation payload as stale and broadcast the
 * flag to any open `/reports/polygon-section-calculation` tab. Called whenever
 * an action changes underlying entities (sub-tool edits, mutation responses,
 * montagestretch saves) so the report banner can prompt regeneration.
 *
 * Lives here (not in `areaMode.ts`) so `map-utils.ts` can import it without
 * creating a circular dependency through `areaMode.ts → map-utils.ts`.
 */
export async function markSectionCalculationCacheStale(): Promise<void> {
	// Push the flag to any open report tab via BroadcastChannel before writing
	// to IDB — `idb-keyval` doesn't emit cross-tab events, so without the
	// broadcast a report tab that's already mounted wouldn't pick up the
	// new stale state until reload.
	broadcastSectionCalculationStale();

	const cached = (await idbGet(POLYGON_SECTION_CALCULATION_IDB_KEY)) as
		| Record<string, unknown>
		| undefined;
	if (!cached) return;
	await idbSet(POLYGON_SECTION_CALCULATION_IDB_KEY, { ...cached, stale: true });
}
